import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY as GET_COMMUNITY_QUERY_KEY } from './useGetCommunity';

export type ApiRequest_Update_Community_Rules = {
  communityId: number;
  rules: string | null;
};

export type ApiResponse_Update_Community_Rules = {
  id: number;
  name: string;
  description: string;
  rules: string | null;
  is_active: boolean;
  require_verification: boolean;
  created_at: string;
  updated_at: string;
};

const METHOD = 'PUT';
const ENDPOINT = 'communities/{communityId}/rules';

export const useUpdateCommunityRules = (options?: {
  onSuccess?: (data: ApiResponse_Update_Community_Rules) => void;
  onError?: (error: Error) => void;
}) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ApiRequest_Update_Community_Rules) => {
      try {
        const data = await handleApiCall<
          ApiResponse_Update_Community_Rules,
          { rules: string | null }
        >(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, { communityId: params.communityId.toString() }),
          { rules: params.rules }
        );

        return data;
      } catch (err) {
        enqueueSnackbar('Failed to update community rules.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: (data) => {
      enqueueSnackbar('Community rules updated successfully!', { variant: 'success' });
      
      // Invalidate the community query to refresh the data
      queryClient.invalidateQueries({ 
        queryKey: GET_COMMUNITY_QUERY_KEY({ id: data.id }) 
      });
      
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
};